import {
  Flex,
  Grid,
  Text,
  useMediaQuery,
  Table,
  Button,
  Image,
} from "@chakra-ui/react";
import { Fragment, useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import { BsCart2 } from "react-icons/bs";

function formatCpfMask(value) {
  if (!value) return "";

  const cpf = value.replace(/\D/g, "");
  const first = cpf.slice(0, 3);
  const last = cpf.slice(-2);

  return `${first}-***-***-${last}`;
}

const formatPhone = (value) => {
  if (!value) return "-";

  const digits = value.toString().replace(/\D/g, "");

  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return digits.replace(/(\d{2})(\d{0,5})(\d{0,4})/, "($1) $2-$3").trim();
};

const normalizeImageUrl = (path) => {
  if (!path) return "";

  const normalized = path.replace(/\\/g, "/");
  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";

  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

export default function Order() {
  const [order, setOrder] = useState(null);
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const { id } = useParams();

  const clientHash = localStorage.getItem("@aqvex/clientHash");
  const sellerId = localStorage.getItem("@aqvex/sellerId");

  const navigate = useNavigate();

  function getTotalProducts(products) {
    if (!Array.isArray(products)) return 0;

    return products.reduce((total, product) => {
      const sizes = product.sizes || {};

      const sizeTotal = Object.values(sizes).reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0,
      );

      return total + sizeTotal;
    }, 0);
  }

  useEffect(() => {
    const getOrder = async () => {
      try {
        const response = await api.get(`/orders/${id}`);
        setOrder(response.data);
      } catch (error) {
        toast.error(error.message ?? "Não foi possível buscar o pedido!");
      }
    };

    getOrder();
  }, []);

  // Agrupa produtos pelo prod_id para exibir todas as cores juntas
  const productGroups = Object.values(
    (order?.products ?? []).reduce((groups, product) => {
      const key = product.prod_id ?? product.id ?? product.name;
      if (!groups[key]) {
        groups[key] = {
          name: product.name,
          prod_id: product.prod_id,
          prod_referencia: product.prod_referencia,
          price: product.price,
          url_imagem_principal: product.url_imagem_principal,
          colors: [],
        };
      }
      groups[key].colors.push(product);
      return groups;
    }, {}),
  );

  return (
    <Flex
      width="100%"
      padding="15px"
      justifyContent="center"
      minH="100vh"
      paddingTop={isLargerThan768 ? "100px" : "120px"}
    >
      <Flex maxW={isLargerThan768 ? "1200px" : "100%"} width="100%">
        {productGroups.length > 0 && (
          <Grid
            templateColumns={isLargerThan768 ? "1fr 0.5fr" : "1fr"}
            columnGap={isLargerThan768 ? "80px" : "0px"}
            width="100%"
          >
            <Flex flexDirection="column" gap="30px" flex={1}>
              {productGroups.map((group) => {
                const mainImage = normalizeImageUrl(group.url_imagem_principal);

                // União de todos os tamanhos de todas as cores deste produto
                const allSizes = [
                  ...new Set(
                    group.colors.flatMap((c) => Object.keys(c.sizes || {})),
                  ),
                ];

                // Usa layout de 3 linhas se qualquer cor tiver requestedQuantity
                const hasRequestedQty = group.colors.some((c) =>
                  Object.keys(c.sizes || {}).some(
                    (s) => c.sizes[s]?.requestedQuantity !== undefined,
                  ),
                );

                const groupTotalQty = group.colors.reduce(
                  (t, c) =>
                    t +
                    Object.values(c.sizes || {}).reduce(
                      (s, i) => s + Number(i.quantity || 0),
                      0,
                    ),
                  0,
                );

                return (
                  <Flex
                    key={group.prod_id ?? group.name}
                    flexDirection="column"
                    paddingBottom="20px"
                    gap="10px"
                  >
                    {/* Cabeçalho do produto */}
                    <Flex alignItems="center" gap="15px">
                      <Image
                        src={mainImage || "/no-image.png"}
                        boxSize="120px"
                        objectFit="cover"
                      />
                      <div>
                        <Text fontSize="20px" fontWeight={700}>
                          {group.name}
                        </Text>
                        <Text fontSize="14px" color="gray.500">
                          Referência: {group.prod_referencia}
                        </Text>
                        <Text fontWeight={600} fontSize="18px" color="green.600">
                          {Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(group.price)}
                        </Text>
                      </div>
                    </Flex>

                    {hasRequestedQty ? (
                      /* Tabela agrupada: cor à esquerda com rowSpan=3, label + tamanhos à direita */
                      <Table.Root size="lg" showColumnBorder>
                        <Table.Header>
                          <Table.Row backgroundColor="white">
                            <Table.ColumnHeader
                              padding="15px"
                              textAlign="center"
                            >
                              Cor
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              padding="15px"
                              textAlign="center"
                            />
                            {allSizes.map((size) => (
                              <Table.ColumnHeader
                                key={size}
                                padding="15px"
                                textAlign="center"
                              >
                                {size}
                              </Table.ColumnHeader>
                            ))}
                          </Table.Row>
                        </Table.Header>

                        <Table.Body>
                          {group.colors.map((color) => {
                            const colorImage = normalizeImageUrl(
                              color.url_imagem_variacao,
                            );

                            return (
                              <Fragment key={color.color}>
                                {/* Pedido (Solicitado) — célula de cor ocupa as 3 linhas */}
                                <Table.Row>
                                  <Table.Cell
                                    rowSpan={3}
                                    padding="15px"
                                    textAlign="center"
                                    verticalAlign="middle"
                                  >
                                    <Flex
                                      w="40px"
                                      h="40px"
                                      borderRadius="full"
                                      bg={
                                        colorImage
                                          ? undefined
                                          : color.cor_hexadecimal
                                      }
                                      backgroundImage={
                                        colorImage
                                          ? `url(${colorImage})`
                                          : undefined
                                      }
                                      backgroundSize="cover"
                                      backgroundPosition="center"
                                      backgroundRepeat="no-repeat"
                                      border="1px solid #ccc"
                                      margin="0 auto"
                                      mb="4px"
                                    />
                                    <Text fontSize="12px">{color.color}</Text>
                                  </Table.Cell>
                                  <Table.Cell padding="15px" textAlign="center">
                                    <Text
                                      fontSize="xs"
                                      color="gray.600"
                                      lineHeight="1.3"
                                    >
                                      Pedido
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      color="gray.600"
                                      lineHeight="1.3"
                                    >
                                      (Solicitado)
                                    </Text>
                                  </Table.Cell>
                                  {allSizes.map((size) => {
                                    const sizeData = color.sizes[size];
                                    const requested =
                                      sizeData?.requestedQuantity ??
                                      sizeData?.quantity ??
                                      0;
                                    return (
                                      <Table.Cell
                                        key={size}
                                        padding="15px"
                                        textAlign="center"
                                      >
                                        <Text fontSize="sm">
                                          {sizeData ? requested : "-"}
                                        </Text>
                                      </Table.Cell>
                                    );
                                  })}
                                </Table.Row>

                                {/* Pedido (Atendido) */}
                                <Table.Row>
                                  <Table.Cell padding="15px" textAlign="center">
                                    <Text
                                      fontSize="xs"
                                      color="gray.600"
                                      lineHeight="1.3"
                                    >
                                      Pedido
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      color="gray.600"
                                      lineHeight="1.3"
                                    >
                                      (Atendido)
                                    </Text>
                                  </Table.Cell>
                                  {allSizes.map((size) => {
                                    const sizeData = color.sizes[size];
                                    return (
                                      <Table.Cell
                                        key={size}
                                        padding="15px"
                                        textAlign="center"
                                      >
                                        <Text fontSize="sm" fontWeight="bold">
                                          {sizeData
                                            ? (sizeData.quantity ?? 0)
                                            : "-"}
                                        </Text>
                                      </Table.Cell>
                                    );
                                  })}
                                </Table.Row>

                                {/* Diferença */}
                                <Table.Row>
                                  <Table.Cell padding="15px" textAlign="center">
                                    <Text
                                      fontSize="xs"
                                      color="gray.600"
                                      lineHeight="1.3"
                                    >
                                      Diferença
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      color="gray.600"
                                      lineHeight="1.3"
                                    >
                                      (Solicitado - Atendido)
                                    </Text>
                                  </Table.Cell>
                                  {allSizes.map((size) => {
                                    const sizeData = color.sizes[size];
                                    if (!sizeData) {
                                      return (
                                        <Table.Cell
                                          key={size}
                                          padding="15px"
                                          textAlign="center"
                                        >
                                          <Text fontSize="sm">-</Text>
                                        </Table.Cell>
                                      );
                                    }
                                    const requested =
                                      sizeData.requestedQuantity ??
                                      sizeData.quantity ??
                                      0;
                                    const attended = sizeData.quantity ?? 0;
                                    const diff = requested - attended;
                                    return (
                                      <Table.Cell
                                        key={size}
                                        padding="15px"
                                        textAlign="center"
                                      >
                                        <Text
                                          fontSize="sm"
                                          color={
                                            diff < 0 ? "red.500" : "inherit"
                                          }
                                          fontWeight={
                                            diff < 0 ? "bold" : "normal"
                                          }
                                        >
                                          {diff}
                                        </Text>
                                      </Table.Cell>
                                    );
                                  })}
                                </Table.Row>
                              </Fragment>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    ) : (
                      /* Layout antigo para pedidos sem requestedQuantity */
                      group.colors.map((color) => {
                        const colorImage = normalizeImageUrl(
                          color.url_imagem_variacao,
                        );
                        const colorSizes = Object.keys(color.sizes || {});

                        return (
                          <Table.Root
                            key={color.color}
                            size="lg"
                            showColumnBorder
                          >
                            <Table.Header>
                              <Table.Row backgroundColor="white">
                                <Table.ColumnHeader
                                  padding="15px"
                                  textAlign="center"
                                >
                                  Cor
                                </Table.ColumnHeader>
                                {colorSizes.map((size) => (
                                  <Table.ColumnHeader
                                    key={size}
                                    padding="15px"
                                    textAlign="center"
                                  >
                                    {size}
                                  </Table.ColumnHeader>
                                ))}
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              <Table.Row minH="60px">
                                <Table.Cell padding="15px" textAlign="center">
                                  <Flex
                                    w="40px"
                                    h="40px"
                                    borderRadius="full"
                                    bg={
                                      colorImage
                                        ? undefined
                                        : color.cor_hexadecimal
                                    }
                                    backgroundImage={
                                      colorImage
                                        ? `url(${colorImage})`
                                        : undefined
                                    }
                                    backgroundSize="cover"
                                    backgroundPosition="center"
                                    backgroundRepeat="no-repeat"
                                    border="1px solid #ccc"
                                    margin="0 auto"
                                  />
                                  <Text fontSize="12px">{color.color}</Text>
                                </Table.Cell>
                                {colorSizes.map((size) => (
                                  <Table.Cell
                                    key={size}
                                    padding="15px"
                                    textAlign="center"
                                  >
                                    <Text fontSize="sm">
                                      {color.sizes[size]?.quantity ?? 0}
                                    </Text>
                                  </Table.Cell>
                                ))}
                              </Table.Row>
                            </Table.Body>
                          </Table.Root>
                        );
                      })
                    )}

                    <Flex>
                      <Text fontSize="14px" fontWeight="600" color="gray.600">
                        {groupTotalQty} {groupTotalQty > 1 ? "pçs" : "pç"}
                      </Text>
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>

            {/* RESUMO */}
            <Flex flexDirection="column" gap="20px">
              <Flex
                padding="15px"
                borderRadius="8px"
                border="1px solid #ececec"
              >
                Pedido{" "}
                <span style={{ fontWeight: 600, marginLeft: "5px" }}>
                  Nº {order?.order_number}
                </span>
              </Flex>
              <Flex
                padding="15px"
                borderRadius="8px"
                border="1px solid #ececec"
                flexDirection="column"
              >
                <Text fontWeight="700">Cliente</Text>
                <Text>{order?.client?.name}</Text>
                <Text>
                  <Text>{formatCpfMask(order?.client?.cpf_cnpj)}</Text>
                </Text>
              </Flex>
              <hr />
              <Text fontWeight="600">Vendedor</Text>
              <Flex gap="10px">
                <Flex
                  width="50px"
                  height="50px"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor="gray"
                  color="white"
                  borderRadius="50%"
                >
                  R
                </Flex>
                <Flex flexDirection="column">
                  <Text fontWeight="600">{order.sellerName ?? "-"}</Text>
                  <Flex alignItems="center" gap="5px">
                    <FaWhatsapp />
                    <Text>
                      {order.sellerCellphone
                        ? formatPhone(order.sellerCellphone)
                        : "-"}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
              <Flex
                padding="15px"
                backgroundColor="rgb(244 244 244)"
                flexDirection="column"
                gap="10px"
              >
                <Flex justifyContent="space-between">
                  <Text>Solicitado</Text>
                  <Flex alignItems="center" gap="10px">
                    <Text>{getTotalProducts(order?.products)} pç</Text>
                    <Text>
                      {Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(order?.totalValue)}
                    </Text>
                  </Flex>
                </Flex>
                <hr />
                <Flex justifyContent="space-between">
                  <Text fontWeight="700">Total</Text>
                  <Text fontWeight="600">
                    {Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(order?.totalValue)}
                  </Text>
                </Flex>
              </Flex>
              <Flex justifyContent="center">
                <Text>COMPRA SEGURA E GARANTIDA</Text>
              </Flex>
              <Button
                variant="outline"
                onClick={() => navigate(`/catalogo/${clientHash}/${sellerId}`)}
                padding="10px"
              >
                <BsCart2 />
                Voltar
              </Button>
            </Flex>
          </Grid>
        )}
      </Flex>
    </Flex>
  );
}
